/**
 * A picture or a video to send over WhatsApp.
 *
 * It uploads the moment a file is chosen, through the same three-step
 * presigned flow every other attachment uses (initiate → PUT straight to
 * storage → complete), so the bytes never pass through the BFF and the
 * progress bar is real. What it hands its parent is the ATTACHMENT, and the
 * parent sends only its id: the storage key is the CRM's internal naming.
 *
 * THE ACCEPTED TYPES ARE EXACTLY WHAT WHATSAPP RENDERS INLINE — JPEG and PNG
 * become an image, MP4 and 3GPP a playable video. Checked here as well as on
 * the server, and the reason is timing: a HEIC photo from an iPhone refused by
 * the server after a 12 MB upload is the same answer delivered a minute later.
 * The server's check is the one that counts; this one is manners.
 */
import { Button } from '@meshhub/design-tokens/ui';
import { Film, ImagePlus, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { formatCount } from '../../lib/format.js';
import { uploadFile } from '../../lib/files.js';
import { S } from '../../lib/strings.js';

/** Mirrors the CRM's MESSAGE_MEDIA_MIME. */
export const MESSAGE_MEDIA_MIME = ['image/jpeg', 'image/png', 'video/mp4', 'video/3gpp'];

/**
 * Mirrors the CRM's 15 MiB cap — 15, not WhatsApp's 16, because the bytes
 * cross two gRPC hops capped at 16 MiB and a file at the limit plus its
 * message fields would not fit in one.
 */
export const MESSAGE_MEDIA_MAX_BYTES = 15 * 1024 * 1024;

export function MediaPicker({ value, onChange, onBusyChange, disabled }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { url, isVideo, name }
  const abortRef = useRef(null);

  // An object URL is a reference the browser holds until told to let go.
  useEffect(() => () => preview && URL.revokeObjectURL(preview.url), [preview]);

  // Leaving mid-upload must stop the PUT, not let it finish into a reservation
  // nobody will ever complete.
  useEffect(() => () => abortRef.current?.abort(), []);

  async function pick(event) {
    // MATERIALISE THE FILE BEFORE RESETTING THE INPUT. A live FileList is
    // emptied by `value = ''`, and a state updater runs too late to have read
    // it — which is exactly how the interaction composer's picker lost every
    // file chosen with the button for two steps (CLAUDE.md §15).
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;

    setError(null);
    if (!MESSAGE_MEDIA_MIME.includes(file.type)) {
      setError(S.messaging.mediaWrongType);
      return;
    }
    if (file.size > MESSAGE_MEDIA_MAX_BYTES) {
      setError(
        S.messaging.mediaTooLarge.replace(
          '{mb}',
          formatCount(Math.round(MESSAGE_MEDIA_MAX_BYTES / (1024 * 1024))),
        ),
      );
      return;
    }

    const next = {
      url: URL.createObjectURL(file),
      isVideo: file.type.startsWith('video/'),
      name: file.name,
    };
    setPreview(next);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const attachment = await uploadFile({
        file,
        ownerType: 'MESSAGE_MEDIA',
        // Owned by nothing that exists yet: the message it travels with is
        // queued AFTER this upload, and a bulk send has no single contact to
        // hang it on. A fresh id per file, which the CRM does not check.
        ownerId: crypto.randomUUID(),
        onProgress: (fraction) => setProgress(fraction),
        signal: controller.signal,
      });
      onChange(attachment);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err.message || S.app.unknownError);
      setPreview(null);
      onChange(null);
    } finally {
      setProgress(null);
      abortRef.current = null;
    }
  }

  function clear() {
    abortRef.current?.abort();
    setPreview(null);
    setError(null);
    setProgress(null);
    onChange(null);
  }

  const uploading = progress !== null;

  // THE PARENT MUST KNOW, because the alternative is a caption sent WITHOUT
  // its picture: attach a video, type a line, press send before the upload
  // finishes — and the form, seeing text and no media yet, would happily send
  // just the text. That is the failure that looks exactly like success.
  useEffect(() => {
    onBusyChange?.(uploading);
  }, [uploading, onBusyChange]);

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={MESSAGE_MEDIA_MIME.join(',')}
        className="hidden"
        onChange={pick}
        aria-label={S.messaging.mediaAdd}
      />

      {!preview && (
        <Button
          variant="ghost"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus size={14} aria-hidden="true" />
          {S.messaging.mediaAdd}
        </Button>
      )}

      {preview && (
        <div className="flex items-start gap-3 rounded border border-border bg-surface-hover p-2">
          {preview.isVideo ? (
            // Muted and without autoplay: this is a check of WHAT was
            // attached, not a playback surface.
            <video
              src={preview.url}
              className="h-mediapreview w-auto rounded"
              controls
              muted
              aria-label={preview.name}
            />
          ) : (
            <img
              src={preview.url}
              alt={preview.name}
              className="h-mediapreview w-auto rounded object-contain"
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
            <span className="flex items-center gap-1 truncate text-text-muted">
              {preview.isVideo && <Film size={12} aria-hidden="true" />}
              <span dir="ltr" className="truncate">
                {preview.name}
              </span>
            </span>
            {uploading ? (
              <span role="status" className="text-text-muted">
                {S.messaging.mediaUploading.replace(
                  '{pct}',
                  formatCount(Math.round(progress * 100)),
                )}
              </span>
            ) : (
              value && <span className="text-success">{S.messaging.mediaReady}</span>
            )}
            <span className="text-text-muted">{S.messaging.mediaCaptionHint}</span>
          </div>
          <Button variant="ghost" onClick={clear} aria-label={S.messaging.mediaRemove}>
            <X size={14} aria-hidden="true" />
          </Button>
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
